import { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';
import { localDateStr, addDaysStr } from '../utils/dates';

// Business Consulting invoice: create from a consulting Work Order, or edit an
// existing one (pass `invoice` with its lines). The total is always the sum of
// the lines - discount + tax; the server recomputes it the same way.
// (2026-10-02)
const EMPTY_LINE = { description: '', period_start: '', period_end: '', amount: '' };

function money(n) {
  return '$' + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function ConsultingInvoiceModal({ workOrder, invoice, onClose, onSaved }) {
  const isEdit = Boolean(invoice?.id);
  const woId = workOrder?.id || invoice?.wo_id;
  const [wo, setWo] = useState(workOrder?.purchase_orders ? workOrder : null);
  const [netTerms, setNetTerms] = useState(null);
  const [form, setForm] = useState(() => isEdit ? {
    invoice_number: invoice.invoice_number, invoice_date: invoice.invoice_date || '', due_date: invoice.due_date || '',
    po_id: invoice.po_id ? String(invoice.po_id) : '', discount_amount: invoice.discount_amount ?? '', tax_amount: invoice.tax_amount ?? '',
    notes: invoice.notes || '',
  } : {
    invoice_number: '', invoice_date: localDateStr(), due_date: '', po_id: '', discount_amount: '', tax_amount: '', notes: '',
  });
  const [lines, setLines] = useState(() => isEdit && invoice.lines?.length
    ? invoice.lines.map(l => ({ description: l.description, period_start: l.period_start || '', period_end: l.period_end || '', amount: String(l.amount) }))
    : [{ ...EMPTY_LINE }]);
  const [dueTouched, setDueTouched] = useState(isEdit);
  const [numberTouched, setNumberTouched] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!woId) return;
    api.get(`/work-orders/${woId}`).then(r => {
      setWo(r.data);
      api.get(`/clients/${r.data.client_id}`).then(c => setNetTerms(c.data.net_terms ?? null)).catch(() => {});
    });
  }, [woId]);

  // Suggested number for the chosen date (the counter is global; the month
  // part follows the invoice date). Only a preview until saved.
  useEffect(() => {
    if (numberTouched || !/^\d{4}-\d{2}-\d{2}$/.test(form.invoice_date)) return;
    api.get(`/consulting/next-number?invoice_date=${form.invoice_date}`)
      .then(r => setForm(f => ({ ...f, invoice_number: r.data.invoice_number })))
      .catch(() => {});
  }, [form.invoice_date, numberTouched]);

  // Due date = invoice date + the client's net terms, unless typed over.
  useEffect(() => {
    if (dueTouched || netTerms == null || !/^\d{4}-\d{2}-\d{2}$/.test(form.invoice_date)) return;
    setForm(f => ({ ...f, due_date: addDaysStr(f.invoice_date, Number(netTerms)) }));
  }, [form.invoice_date, netTerms, dueTouched]);

  const totals = useMemo(() => {
    const subtotal = Math.round(lines.reduce((s, l) => s + (Number(l.amount) || 0), 0) * 100) / 100;
    const total = Math.round((subtotal - (Number(form.discount_amount) || 0) + (Number(form.tax_amount) || 0)) * 100) / 100;
    return { subtotal, total };
  }, [lines, form.discount_amount, form.tax_amount]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setLine = (i, k, v) => setLines(ls => ls.map((l, j) => j === i ? { ...l, [k]: v } : l));
  const pos = (wo?.purchase_orders || []).filter(p => !p.deleted_at);

  async function save() {
    setError('');
    if (lines.some(l => !l.description.trim() || l.amount === '' || isNaN(Number(l.amount)))) {
      setError('Every line needs a description and an amount');
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      po_id: form.po_id ? Number(form.po_id) : null,
      lines: lines.map(l => ({ ...l, amount: Number(l.amount), period_start: l.period_start || null, period_end: l.period_end || null })),
    };
    try {
      const r = isEdit
        ? await api.put(`/consulting/invoices/${invoice.id}`, payload)
        : await api.post(`/consulting/work-orders/${woId}/invoices`, payload);
      onSaved?.(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save the invoice');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="import-modal" style={{ width: 760, maxWidth: '96vw' }} onClick={e => e.stopPropagation()}>
        <div className="drawer-header">
          <h3 className="drawer-title">{isEdit ? `Edit ${invoice.invoice_number}` : 'New consulting invoice'}</h3>
          <button className="drawer-close" onClick={onClose}>×</button>
        </div>
        <div style={{ padding: 24, maxHeight: '75vh', overflowY: 'auto' }}>
          {wo && (
            <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12, marginBottom: 16, fontSize: 13 }}>
              Business Consulting · <strong>{wo.client_name}</strong> · WO {wo.client_wo_number}
              {netTerms != null && <> · Net {netTerms}</>}
            </div>
          )}
          {error && (
            <div style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 16 }}>{error}</div>
          )}

          <div className="form-grid">
            <div className="form-field">
              <label>Invoice date</label>
              <input type="date" value={form.invoice_date} onChange={e => set('invoice_date', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Invoice number</label>
              <input value={form.invoice_number} onChange={e => { setNumberTouched(true); set('invoice_number', e.target.value); }} placeholder="TMV/MMM/YYYY-NNNN" />
              <span className="form-hint">From the consulting counter (1001+, runs across months). Editable; must be unique.</span>
            </div>
            <div className="form-field">
              <label>Client PO <span className="form-hint">(optional)</span></label>
              <select value={form.po_id} onChange={e => set('po_id', e.target.value)}>
                <option value="">No PO - billed on the Work Order</option>
                {pos.map(p => <option key={p.id} value={p.id}>{p.po_number} ({p.period_start} – {p.period_end})</option>)}
              </select>
            </div>
            <div className="form-field">
              <label>Due date</label>
              <input type="date" value={form.due_date} onChange={e => { setDueTouched(true); set('due_date', e.target.value); }} />
              <span className="form-hint">Invoice date + client net terms unless changed.</span>
            </div>
          </div>

          <h4 style={{ fontSize: 13, fontWeight: 700, margin: '18px 0 8px' }}>Line items</h4>
          <table className="data-table" style={{ marginBottom: 8 }}>
            <thead>
              <tr><th style={{ width: 28 }}>#</th><th>Description</th><th style={{ width: 130 }}>Period from</th><th style={{ width: 130 }}>Period to</th><th style={{ width: 120, textAlign: 'right' }}>Amount</th><th style={{ width: 30 }} /></tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td><input value={l.description} onChange={e => setLine(i, 'description', e.target.value)} placeholder="e.g. Business Consulting Services for Aug 2026" style={{ width: '100%' }} /></td>
                  <td><input type="date" value={l.period_start} onChange={e => setLine(i, 'period_start', e.target.value)} /></td>
                  <td><input type="date" value={l.period_end} onChange={e => setLine(i, 'period_end', e.target.value)} /></td>
                  <td><input type="number" step="0.01" value={l.amount} onChange={e => setLine(i, 'amount', e.target.value)} style={{ width: '100%', textAlign: 'right' }} /></td>
                  <td>{lines.length > 1 && <button className="btn btn-ghost btn-sm" title="Remove line" onClick={() => setLines(ls => ls.filter((_, j) => j !== i))}>×</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="btn btn-secondary btn-sm" onClick={() => setLines(ls => [...ls, { ...EMPTY_LINE }])}>+ Add line</button>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 240px', gap: 20, marginTop: 18 }}>
            <div className="form-field">
              <label>Notes (internal)</label>
              <textarea rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} />
            </div>
            <div style={{ fontSize: 13 }}>
              <Row label="Subtotal" value={money(totals.subtotal)} />
              <div className="form-field" style={{ margin: '6px 0' }}>
                <label>Discount (optional)</label>
                <input type="number" step="0.01" min="0" value={form.discount_amount} onChange={e => set('discount_amount', e.target.value)} placeholder="N/A" />
              </div>
              <div className="form-field" style={{ margin: '6px 0' }}>
                <label>Tax (optional)</label>
                <input type="number" step="0.01" min="0" value={form.tax_amount} onChange={e => set('tax_amount', e.target.value)} placeholder="N/A" />
              </div>
              <Row label={<strong>Total</strong>} value={<strong>{money(totals.total)}</strong>} />
            </div>
          </div>
        </div>
        <div className="drawer-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create invoice (Draft)'}</button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
      <span style={{ color: 'var(--muted)' }}>{label}</span><span>{value}</span>
    </div>
  );
}
